import { useCallback, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { RootState } from '@/store/store';

import { Flow, StepResult } from '../models';
import { runFlow } from '../utils/runner';
import { buildRunVariables } from '../utils/template';

export const useRunner = () => {
  const sessionToken = useSelector(
    (state: RootState) => state.auth.auth.accessToken
  );

  const [results, setResults] = useState<StepResult[]>([]);
  const [variables, setVariables] = useState<Record<string, string>>({});
  const [running, setRunning] = useState(false);
  const stopRequested = useRef(false);

  const run = useCallback(
    async (flow: Flow) => {
      setRunning(true);
      setResults([]);
      stopRequested.current = false;

      const initial = buildRunVariables(flow.variables);
      setVariables(initial);

      const finalVariables = await runFlow(
        flow,
        { sessionToken: sessionToken ?? '', variables: initial },
        {
          onStepStart: (result) =>
            setResults((current) => [...current, result]),
          onStepEnd: (result, updated) => {
            setResults((current) =>
              current.map((item) =>
                item.stepId === result.stepId ? result : item
              )
            );
            setVariables(updated);
          },
        },
        () => stopRequested.current
      );

      setVariables(finalVariables);
      setRunning(false);
      return finalVariables;
    },
    [sessionToken]
  );

  const stop = useCallback(() => {
    stopRequested.current = true;
  }, []);

  const reset = useCallback(() => {
    setResults([]);
    setVariables({});
  }, []);

  return { results, variables, running, run, stop, reset };
};
